import React, { useState, useEffect } from 'react';
import { Bot, Sparkles, Key, Save, CheckCircle, AlertCircle, Eye, EyeOff, RefreshCw, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import { aiExpedientesAPI } from '../api';

const AI_PROVIDERS = {
  CHATGPT: 'chatgpt',
  GEMINI: 'gemini',
  DEEPSEEK: 'deepseek'
};

const AIExpedienteSettings = () => {
  const [settings, setSettings] = useState({
    provider: AI_PROVIDERS.CHATGPT,
    geminiApiKey: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await aiExpedientesAPI.getSettings();
      if (response.data) {
        setSettings(prev => ({
          ...prev,
          provider: response.data.provider || AI_PROVIDERS.CHATGPT,
          geminiApiKey: response.data.geminiApiKey || ''
        }));
      }
    } catch (error) {
      console.error('Error cargando configuración IA Expedientes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await aiExpedientesAPI.updateSettings(settings);
      toast({ 
        title: "Configuración guardada", 
        description: "Los ajustes de IA para expedientes se han guardado correctamente" 
      });
    } catch (error) {
      toast({ 
        title: "Error", 
        description: "No se pudo guardar la configuración", 
        variant: "destructive" 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await aiExpedientesAPI.testConnection({
        provider: settings.provider,
        geminiApiKey: settings.geminiApiKey
      });
      setTestResult({ 
        success: true, 
        message: response.data.response?.substring(0, 200) + "..." 
      });
      toast({ 
        title: "Test exitoso", 
        description: "La IA de expedientes está funcionando correctamente" 
      });
    } catch (error) {
      setTestResult({ 
        success: false, 
        message: error.response?.data?.detail || "Error de conexión" 
      });
      toast({ 
        title: "Error en test", 
        description: "No se pudo conectar con el proveedor de IA", 
        variant: "destructive" 
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Descripción */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900">IA para Análisis de Expedientes</h3>
              <p className="text-sm text-blue-700 mt-1">
                Esta configuración controla el proveedor de IA utilizado específicamente para las funciones de análisis de expedientes:
              </p>
              <ul className="text-sm text-blue-600 mt-2 space-y-1">
                <li>• Resumen automático de actuaciones</li>
                <li>• Análisis de probabilidad de éxito</li>
                <li>• Sugerencias de estrategia legal</li>
                <li>• Chat contextualizado por expediente</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selector de Proveedor */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-blue-600" />
            Proveedor de IA para Expedientes
          </CardTitle>
          <CardDescription>
            Selecciona el modelo de IA para el análisis de expedientes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* ChatGPT Option */}
            <div 
              onClick={() => setSettings({...settings, provider: AI_PROVIDERS.CHATGPT})}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                settings.provider === AI_PROVIDERS.CHATGPT 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold">ChatGPT (GPT-4o)</h3>
                  <Badge variant="outline" className="text-xs">Recomendado</Badge>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Modelo avanzado de OpenAI con excelente comprensión legal y análisis detallado
              </p>
              {settings.provider === AI_PROVIDERS.CHATGPT && (
                <div className="mt-3 p-2 bg-blue-100 rounded-lg text-xs text-blue-700">
                  ✓ Usa la API Key de OpenAI configurada en el servidor
                </div>
              )}
            </div>

            {/* Gemini Option */}
            <div 
              onClick={() => setSettings({...settings, provider: AI_PROVIDERS.GEMINI})}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                settings.provider === AI_PROVIDERS.GEMINI 
                  ? 'border-purple-500 bg-purple-50' 
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Bot className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Google Gemini</h3>
                  <Badge variant="outline" className="text-xs">Búsqueda Web</Badge>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Modelo de Google con búsqueda integrada y acceso a información actualizada
              </p>
            </div>

            {/* DeepSeek Option */}
            <div 
              onClick={() => setSettings({...settings, provider: AI_PROVIDERS.DEEPSEEK})}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                settings.provider === AI_PROVIDERS.DEEPSEEK 
                  ? 'border-cyan-500 bg-cyan-50' 
                  : 'border-gray-200 hover:border-cyan-300'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <h3 className="font-semibold">DeepSeek V3</h3>
                  <Badge variant="outline" className="text-xs bg-cyan-100">Económico</Badge>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Modelo potente y económico, ideal para análisis legal extensivo
              </p>
              {settings.provider === AI_PROVIDERS.DEEPSEEK && (
                <div className="mt-3 p-2 bg-cyan-100 rounded-lg text-xs text-cyan-700">
                  ✓ Usa la API Key de DeepSeek configurada en el servidor
                </div>
              )}
            </div>
          </div>

          {/* Gemini API Key */}
          {settings.provider === AI_PROVIDERS.GEMINI && (
            <div className="mt-4 p-4 bg-purple-50 rounded-xl border border-purple-200">
              <Label className="flex items-center gap-2 mb-2">
                <Key className="w-4 h-4" />
                API Key de Google Gemini
              </Label>
              <div className="relative">
                <Input
                  type={showApiKey ? "text" : "password"}
                  value={settings.geminiApiKey}
                  onChange={(e) => setSettings({...settings, geminiApiKey: e.target.value})}
                  placeholder="AIzaSy..."
                  className="pr-10"
                />
                <button 
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Obtén tu API key en{' '}
                <a 
                  href="https://aistudio.google.com/apikey" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-purple-600 hover:underline"
                >
                  Google AI Studio
                </a>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test & Save */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              variant="outline" 
              onClick={handleTest}
              disabled={testing || (settings.provider === AI_PROVIDERS.GEMINI && !settings.geminiApiKey)}
              className="flex-1"
            >
              {testing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Probando...</>
              ) : (
                <><Bot className="w-4 h-4 mr-2" />Probar Conexión</>
              )}
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" />Guardar Configuración</>
              )}
            </Button>
          </div>

          {/* Test Result */}
          {testResult && (
            <div className={`mt-4 p-4 rounded-lg ${
              testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-start gap-2">
                {testResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                )}
                <div>
                  <p className={`font-medium ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    {testResult.success ? 'Conexión exitosa' : 'Error de conexión'}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">{testResult.message}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AIExpedienteSettings;
